import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/use-translations";
import { api, isAgentBusy, isDeletionBlocked, isStaleAnswer, type ChatHistoryMessage } from "@/lib/api";

interface UseWorkspaceChatOptions {
	projectId: string | undefined;
	teamId: string | undefined;
	monitoringReviewId: string | undefined;
	monitoringNotificationId: string | undefined;
	/** Called before sending: while the agent is waiting, a message is its answer to the question whose id it returns. */
	clearQuestion: () => string | undefined;
	/** Reconnects to the agent's questions; the server re-sends the one still waiting. */
	resyncQuestion: () => void;
	/** Files (and the brief) attached to a typed message. */
	getSelectedFileIds: () => string[];
}

/** The composer's text and sending a message with an optimistic update. */
export function useWorkspaceChat({
	projectId,
	teamId,
	monitoringReviewId,
	monitoringNotificationId,
	clearQuestion,
	resyncQuestion,
	getSelectedFileIds,
}: UseWorkspaceChatOptions) {
	const t = useTranslations("workspace");
	const queryClient = useQueryClient();
	const [chatInput, setChatInput] = useState("");

	const chatMutation = useMutation({
		mutationFn: ({
			message,
			fileIds,
			questionId,
		}: {
			message: string;
			fileIds: string[];
			questionId?: string;
		}) =>
			api.sendChatMessage(
				projectId!,
				message,
				fileIds,
				teamId,
				monitoringReviewId,
				monitoringNotificationId,
				questionId,
			),
		onMutate: async ({ message }) => {
			await queryClient.cancelQueries({ queryKey: ["chatHistory", projectId] });
			const previousMessages = queryClient.getQueryData<ChatHistoryMessage[]>([
				"chatHistory",
				projectId,
			]);

			if (previousMessages) {
				queryClient.setQueryData<ChatHistoryMessage[]>(
					["chatHistory", projectId],
					[
						...previousMessages,
						{
							id: `temp-${Date.now()}`,
							conversationId: projectId!,
							parentId: null,
							role: "user",
							content: message,
							toolCall: null,
							toolCallId: null,
							tokenUsed: null,
							createdAt: new Date().toISOString(),
						},
					],
				);
			}

			return { previousMessages };
		},
		onSuccess: async () => {
			// Add a slight delay to simulate thinking for better UX
			await new Promise((resolve) => setTimeout(resolve, 600));
			queryClient.invalidateQueries({ queryKey: ["chatHistory", projectId] });
		},
		onError: (err, sent, context) => {
			if (context?.previousMessages) {
				queryClient.setQueryData(
					["chatHistory", projectId],
					context.previousMessages,
				);
			}

			// An agent mid-run refuses the message — expected, not a failure. Text in the interface language, not the server's.
			if (isAgentBusy(err)) {
				toast.info(t("agentBusy"));
				return;
			}
			// A deletion waits for its buttons: the message is kept for later.
			if (isDeletionBlocked(err)) {
				toast.info(t("deletion.blocked"));
				setChatInput((current) => current || sent.message);
				return;
			}
			// The answer was for a question that is gone or unknown to this page: show the current one.
			if (isStaleAnswer(err)) {
				toast.info(t("questionChanged"));
				// Give the typed text back unless something new was typed meanwhile.
				setChatInput((current) => current || sent.message);
				resyncQuestion();
				return;
			}

			toast.error(
				err instanceof Error ? err.message : "Failed to send message",
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["chatHistory", projectId] });
		},
	});

	const handleSendMessage = (e: FormEvent) => {
		e.preventDefault();
		if (!chatInput.trim() || !projectId) return;

		const selectedFileIds = getSelectedFileIds();

		// While the agent is waiting, this message becomes its answer
		const questionId = clearQuestion();
		chatMutation.mutate({
			message: chatInput.trim(),
			fileIds: selectedFileIds,
			questionId,
		});
		setChatInput("");
	};

	const handleAgentOptionSelect = (label: string) => {
		if (!projectId) return;
		const questionId = clearQuestion();
		chatMutation.mutate({ message: label, fileIds: [], questionId });
	};

	return {
		chatInput,
		setChatInput,
		isSending: chatMutation.isPending,
		handleSendMessage,
		handleAgentOptionSelect,
	};
}
